import { SportsRugbySharp } from "@mui/icons-material";
import { createTheme, useTheme, ThemeOptions, Components } from "@mui/material";
import { TypographyOptions } from "@mui/material/styles/createTypography";
import React from "react";
import { thTH } from "@mui/material/locale";
declare module "@mui/material" {
    interface Palette {
        tertiary: Palette["primary"];
    }
    interface PaletteOptions {
        tertiary: PaletteOptions["primary"];
    }
    interface TypographyPropsVariantOverrides {
        title1: true;
        title2: true;
        display1: true;
        headline1: true;
        headline2: true;
    }
}

interface ExtendedTypographyOptions extends TypographyOptions {
    title1: React.CSSProperties;
    title2: React.CSSProperties;
    display1: React.CSSProperties;
    headline1: React.CSSProperties;
    headline2: React.CSSProperties;
}

const palette = {
    primary: {
        dark: "#008C45",
        main: "#1FAE5C",
        light: "#DFFFEE",
        lighter: "#F9FDFB",
        contrastText: "#FFFFFF"
    },
    secondary: {
        dark: "#C0962A",
        main: "#D6AA3D",
        light: "#EAC15A"
    },
    tertiary: {
        dark: "#1976D2",
        main: "#109CF1",
        light: "#D5EFFF"
    },
    info: {
        dark: "#1976D2",
        main: "#109CF1",
        light: "#D5EFFF"
    },
    success: {
        dark: "#008C45",
        main: "#1FAE5C",
        light: "#E0E0E0"
    },
    warning: {
        dark: "#C0962A",
        main: "#D6AA3D",
        light: "#EAC15A"
    },
    error: {
        dark: "#B3261E",
        main: "#FF0000",
        light: "#FFE4E4"
    },
    black: {
        dark: "#171717",
        main: "#444444",
        light: "#9FA2B4"
    },
    text: {
        primary: "#171717",
        secondary: "#444444",
        tertiary: "#9FA2B4",
        white: "#fff"
    },
    border: {
        main: "#E0E0E0;",
        primary: "#E0E0E0;"
    },
    divider: "rgba(0,0,0,0.12)",
    background: {
        paper: "#fff",
        default: "#fff",
        light: "#F7F9FA"
    },
    grey: {
        "100":"#818181",
        "200": "#E0E0E0",
        "300": "#F1F1F1",
        "400": "#E3E3E3",
        "500": "#D6D6D6",
        "600": "#B9B9B9",
        "800": "#848484",
        "900": "#545454"
    },
    green: {
        '100': '#007E11',
        '200': '#147922',
    }
};

const breakpoints = {
    values: {
        xs: 0,
        sm: 600,
        md: 905,
        lg: 1280,
        xl: 1440
    }
};

const spacing = (factor: number) => `${0.5 * factor}rem`;

const components = {
    MuiPaper: {
        styleOverrides: {
            root: {
                overflow: "auto"
            }
        }
    },
    MuiTypography: {
        defaultProps: {
            lineHeight: "102%"
        }
    },
    MuiButton: {
        defaultProps: {
            size: "small"
        },
        styleOverrides: {
            root: {
                fontSize: "1.125rem"
            }
        }
    },
    MuiFormControl: {
        defaultProps: {
            fullWidth: true
        },
        styleOverrides: {
            root: {
                width: "100%",
                "& .MuiInputBase-root": {
                    borderRadius: "4px",
                    backgroundColor: "white"
                },
                "& .MuiFormHelperText-root": {
                    lineHeight: "120%",
                    background: "white",
                    transform: "translateY(-0.28rem)",
                    position: "absolute !important",
                    top: "80%",
                    left: "0"
                },
                "& .MuiInputBase-multiline + .MuiFormHelperText-root": {
                    top: "92%"
                }
            }
        }
    }
} as Components;

const typography = {
    title1: {
        display: "block",
        fontWeight: 700,
        // 24px
        fontSize: "1.5rem",
        color: palette.text.primary
    },
    title2: {
        display: "block",
        fontWeight: 700,
        // 20px
        fontSize: "1.25rem",
        color: palette.text.primary
    },
    display1: {
        display: "block",
        fontWeight: 700,
        // 48px
        fontSize: "3rem",
        color: palette.text.primary
    },
    headline1: {
        display: "block",
        // 18px
        fontSize: "1.25rem",
        color: palette.text.primary
    },
    headline2: {
        display: "block",
        // 16px
        fontSize: "1rem",
        color: palette.text.primary
    }
} as TypographyOptions;

export default createTheme({ palette, breakpoints, typography, components, spacing }, thTH);
